import { NLPSearchResultFormat, type NLPSearchType } from '../../../../ai-answer-dialog/types';
import { type CreateDefinitionDetailsType } from '../../gql/create-definition-mutation';
import { type ConfluenceScopeType, EditedDefinitionScope, type JiraScopeType } from '../types';

export const getQuerySpecsPerEntity = (query: string) => {
	const pageOrBlogRegex = /spaces\/.*\/(pages|blog)/;
	const spaceRegex = /https:\/\/[^\/]+\/wiki\/spaces\/(?!.*\/(pages|blog)).*/;
	// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
	const confluenceTinyRegex = /\/wiki\/x\/(?<resourceId>[^\/(?)]+)$/;
	const queryParts = query.split('/');
	const queryParamsRemoved = query.split('?')[0];

	if (query.match(pageOrBlogRegex)) {
		// e.g. https://hello.atlassian.net/wiki/spaces/AAI/pages/3665fakeId/Test+Page+Title
		return {
			searchQuery: queryParts[queryParts.length - 1],
			entities: ['ati:cloud:confluence:page', 'ati:cloud:confluence:blogpost'],
			spacesFilter: undefined,
		};
	} else if (query.match(spaceRegex)) {
		// e.g. https://hello.atlassian.net/wiki/spaces/SK1/overview
		const spacesIndex = queryParts.indexOf('spaces');
		return {
			searchQuery: '',
			entities: ['ati:cloud:confluence:space'],
			spacesFilter: [spacesIndex > -1 ? queryParts[spacesIndex + 1] : ''],
		};
	} else if (queryParamsRemoved.match(confluenceTinyRegex)) {
		// e.g. https://hello.atlassian.net/wiki/x/4c942w
		return {
			searchQuery: '',
			entities: ['ati:cloud:confluence:page', 'ati:cloud:confluence:blogpost'],
			spacesFilter: undefined,
			needsLinkResolution: true,
		};
	}
	return {
		searchQuery: query,
		entities: [
			'ati:cloud:confluence:page',
			'ati:cloud:confluence:blogpost',
			'ati:cloud:confluence:space',
		],
		spacesFilter: undefined,
	};
};

export const getEntityIdInScope = ({
	selectedScope,
	productScopes,
	cloudId,
}: {
	selectedScope: EditedDefinitionScope;
	productScopes: ConfluenceScopeType | JiraScopeType;
	cloudId: string;
}): string => {
	let contentARI = '';
	let spaceARI = '';
	let projectARI = '';
	if ('contentARI' in productScopes) {
		contentARI = productScopes.contentARI;
		spaceARI = productScopes.spaceARI;
	}
	if ('projectARI' in productScopes) {
		projectARI = productScopes.projectARI;
	}

	switch (selectedScope) {
		case EditedDefinitionScope.PAGE:
		case EditedDefinitionScope.BLOGPOST:
			return contentARI;
		case EditedDefinitionScope.SPACE:
			return spaceARI;
		case EditedDefinitionScope.PROJECT:
			return projectARI;
		default:
			return cloudId;
	}
};

export const mapCreatedDefinitionToNLPSearchType = (
	definitionDetails: CreateDefinitionDetailsType,
): NLPSearchType => {
	const definitionReferenceDetails = definitionDetails.confluenceEntity;

	return {
		nlpResult: definitionDetails.definition,
		uniqueSources: definitionDetails.confluenceEntity
			? [
					{
						id: definitionReferenceDetails?.id || '',
						title: definitionReferenceDetails?.title || '',
						url: definitionDetails.referenceUrl || '',
						type: definitionReferenceDetails?.type || '',
						// for now we only allow users to add a source from Confluence
						product: 'Confluence',
						lastModified: '',
						iconUrl: '',
						spaceName: definitionReferenceDetails?.space?.name || '',
						spaceUrl: null,
					},
				]
			: [],
		nlpResultEditor: {
			name: definitionDetails.editor?.name || '',
			id: definitionDetails.editor?.accountId || '',
		},
		disclaimer: null,
		errorState: null,
		format: NLPSearchResultFormat.MARKDOWN,
	};
};

// Specifically checking for the \u200c character (ZERO WIDTH NON-JOINER) as the editor conditionally adds it.
// experienced an issue when adding multiple spaces without other characters, the \u200c character was added,
// throwing up the validation that an empty definition was valid CCSEARCH-4472
export const removeInvisibleCharacters = (str: string): string => {
	return str.replace(/[\u200c]/g, '');
};
